/**
 * Mid-session overlay when the trainer loses LiveKit / internet.
 */

import React from "react";
import { WifiOff, X } from "lucide-react";

type OfflineDisconnectModalProps = {
  open: boolean;
  title: string;
  reason: string;
  reconnecting?: boolean;
  onContinueHere: () => void;
  onClose: () => void;
};

export function OfflineDisconnectModal({
  open,
  title,
  reason,
  reconnecting = false,
  onContinueHere,
  onClose,
}: OfflineDisconnectModalProps) {
  if (!open) return null;

  return (
    <div
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="offline-disconnect-title"
      aria-describedby="offline-disconnect-reason"
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 240,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "rgba(0,0,0,0.72)",
        backdropFilter: "blur(4px)",
        padding: 16,
      }}
    >
      <div
        style={{
          width: "100%",
          maxWidth: 420,
          borderRadius: 16,
          border: "1px solid rgba(248,113,113,0.35)",
          background: "#1a1d26",
          padding: 20,
          boxShadow: "0 24px 48px rgba(0,0,0,0.5)",
          position: "relative",
        }}
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          style={{
            position: "absolute",
            top: 12,
            right: 12,
            border: "none",
            background: "transparent",
            color: "#94a3b8",
            cursor: "pointer",
            padding: 4,
            lineHeight: 0,
          }}
        >
          <X size={18} />
        </button>
        <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
          <div
            style={{
              width: 36,
              height: 36,
              borderRadius: 10,
              background: "rgba(239,68,68,0.16)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <WifiOff size={18} color="#f87171" />
          </div>
          <div style={{ paddingRight: 20 }}>
            <h2
              id="offline-disconnect-title"
              style={{ margin: 0, fontSize: 18, fontWeight: 700, color: "#fff" }}
            >
              {title}
            </h2>
            <p
              id="offline-disconnect-reason"
              style={{
                margin: "8px 0 0",
                fontSize: 13,
                color: "#cbd5e1",
                lineHeight: 1.5,
              }}
            >
              {reason}
            </p>
            {reconnecting ? (
              <p style={{ margin: "8px 0 0", fontSize: 12, color: "#fbbf24" }}>
                Trying to reconnect…
              </p>
            ) : null}
          </div>
        </div>

        <div
          style={{
            marginTop: 18,
            display: "flex",
            gap: 8,
          }}
        >
          <button
            type="button"
            onClick={onClose}
            style={{
              flex: 1,
              border: "1px solid rgba(255,255,255,0.14)",
              borderRadius: 10,
              padding: "10px 14px",
              background: "transparent",
              color: "#cbd5e1",
              fontWeight: 700,
              fontSize: 13,
              cursor: "pointer",
            }}
          >
            Close
          </button>
          <button
            type="button"
            onClick={onContinueHere}
            style={{
              flex: 2,
              border: "none",
              borderRadius: 10,
              padding: "10px 14px",
              background: "#2563eb",
              color: "#fff",
              fontWeight: 700,
              fontSize: 13,
              cursor: "pointer",
            }}
          >
            Continue here
          </button>
        </div>
      </div>
    </div>
  );
}
